/**
 * Exponential backoff polling utility with idle detection
 * Reduces CPU wake-ups and battery drain by gradually increasing poll intervals
 * Pauses polling when page is hidden to save resources
 */

interface PollingOptions {
  initialInterval?: number;
  maxInterval?: number;
  backoffMultiplier?: number;
  onPoll: () => void | Promise<void>;
  pauseWhenHidden?: boolean;
}

export class ExponentialBackoffPoller {
  private intervalId: number | null = null;
  private currentInterval: number;
  private readonly initialInterval: number;
  private readonly maxInterval: number;
  private readonly backoffMultiplier: number;
  private readonly onPoll: () => void | Promise<void>;
  private readonly pauseWhenHidden: boolean;
  private isPolling = false;
  private isPaused = false;
  private visibilityHandler: (() => void) | null = null;

  constructor({
    initialInterval = 30000, // 30s
    maxInterval = 300000, // 5min
    backoffMultiplier = 1.5,
    onPoll,
    pauseWhenHidden = true,
  }: PollingOptions) {
    this.initialInterval = initialInterval;
    this.currentInterval = initialInterval;
    this.maxInterval = maxInterval;
    this.backoffMultiplier = backoffMultiplier;
    this.onPoll = onPoll;
    this.pauseWhenHidden = pauseWhenHidden;
  }

  start() {
    if (this.isPolling) return;
    
    this.isPolling = true;
    this.currentInterval = this.initialInterval;
    
    // Set up visibility change listener
    if (this.pauseWhenHidden) {
      this.visibilityHandler = () => {
        if (document.hidden) {
          this.pause();
        } else {
          this.resume();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    
    this.scheduleNext();
  }

  stop() {
    if (this.intervalId !== null) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
    this.isPolling = false;
    this.isPaused = false;
    this.currentInterval = this.initialInterval;
    
    // Clean up visibility listener
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private pause() {
    if (this.intervalId !== null) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
    this.isPaused = true;
  }

  private resume() {
    if (!this.isPaused || !this.isPolling) return;
    this.isPaused = false;
    this.reset(); // Reset to initial interval on resume
    this.scheduleNext();
  }

  private scheduleNext() {
    if (!this.isPolling || this.isPaused) return;

    this.intervalId = window.setTimeout(async () => {
      // Check if page is hidden before polling
      if (!document.hidden) {
        await this.onPoll();
      }
      
      // Increase interval for next poll (exponential backoff)
      this.currentInterval = Math.min(
        this.currentInterval * this.backoffMultiplier,
        this.maxInterval
      );
      
      this.scheduleNext();
    }, this.currentInterval);
  }

  reset() {
    this.currentInterval = this.initialInterval;
  }
}
